import { useEffect } from "react";
import { X, ArrowRight, ChevronDown, Instagram } from "lucide-react";
import { SITE } from "@/lib/site-data";

/**
 * Full-screen luxury service detail overlay.
 * Not using shadcn Dialog because we want a full-page editorial layout.
 */
export default function ServiceDetail({ service, open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!service) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`service-title-${service.id}`}
      data-testid={`service-detail-${service.id}`}
      className={`fixed inset-0 z-[100] transition-opacity duration-500 ${
        open
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#050505]/95 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="absolute inset-0 overflow-y-auto">
        <div className="relative min-h-full bg-[color:var(--sr-black)]">
          {/* Close */}
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="service-detail-close"
            className="fixed top-6 right-6 z-10 w-10 h-10 rounded-full border border-[var(--sr-border-strong)] flex items-center justify-center text-[color:var(--sr-ivory)] hover:border-[color:var(--sr-gold)] hover:text-[color:var(--sr-gold)] bg-[#050505]/80 backdrop-blur transition-colors"
          >
            <X size={16} />
          </button>

          {/* Hero image */}
          <div className="relative w-full h-[60vh] lg:h-[70vh] overflow-hidden">
            <img
              src={service.image}
              alt={service.imageAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
            <div className="absolute bottom-8 left-0 right-0">
              <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
                <p className="eyebrow text-[color:var(--sr-gold)]">
                  Signature Service
                </p>
                <h2
                  id={`service-title-${service.id}`}
                  className="font-serif text-4xl sm:text-5xl lg:text-[64px] text-[color:var(--sr-ivory)] mt-3 tracking-[-0.01em] leading-[1.02]"
                >
                  {service.title}
                </h2>
                <p className="mt-4 text-[color:var(--sr-beige)] text-lg italic font-serif">
                  {service.tagline}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-[1000px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
            {/* Intro */}
            <p className="text-[color:var(--sr-ivory)]/90 text-[16px] leading-[1.85] max-w-[720px]">
              {service.intro}
            </p>

            {/* Why choose */}
            <div className="mt-16 pt-14 border-t border-[var(--sr-border)]">
              <p className="eyebrow">The Skin Refinery Difference</p>
              <h3 className="font-serif text-3xl lg:text-[38px] text-[color:var(--sr-ivory)] mt-3 tracking-[-0.005em]">
                {service.whyTitle}
              </h3>
              <p className="mt-5 text-[color:var(--sr-beige)] text-[15px] leading-[1.85] max-w-[720px]">
                {service.whyIntro}
              </p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {service.benefits.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[color:var(--sr-ivory)] text-[14.5px] leading-[1.65]"
                  >
                    <span className="mt-2 h-[1px] w-4 bg-[color:var(--sr-gold)] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing / CTA */}
            <div className="mt-16 pt-14 border-t border-[var(--sr-border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="eyebrow">Ready to Book?</p>
                <p className="font-serif text-2xl text-[color:var(--sr-ivory)] mt-2 tracking-[-0.005em]">
                  Real-time availability &amp; pricing on my booking site.
                </p>
              </div>
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-book-${service.id}`}
                className="btn-gold shrink-0"
              >
                Book Now <ArrowRight size={14} strokeWidth={1.5} />
              </a>
            </div>

            {/* Portfolio gallery */}
            {service.portfolio && service.portfolio.length > 0 && (
              <div className="mt-16 pt-14 border-t border-[var(--sr-border)]">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                  <div>
                    <p className="eyebrow">Recent Work</p>
                    <h3 className="font-serif text-3xl lg:text-[38px] text-[color:var(--sr-ivory)] mt-3 tracking-[-0.005em]">
                      Portfolio
                    </h3>
                  </div>
                  <a
                    href={SITE.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="eyebrow text-[color:var(--sr-gold)] hover:opacity-80 transition-opacity"
                    data-testid={`portfolio-instagram-${service.id}`}
                  >
                    See more on Instagram →
                  </a>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-4">
                  {service.portfolio.map((p, i) => (
                    <figure
                      key={p.src}
                      className={`zoom-img bg-[#0a0a0a] overflow-hidden ${
                        i === 0
                          ? "col-span-2 md:col-span-2 md:row-span-2 aspect-square md:aspect-auto"
                          : "aspect-square"
                      }`}
                      data-testid={`portfolio-item-${service.id}-${i}`}
                    >
                      <img
                        src={p.src}
                        alt={p.alt}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </figure>
                  ))}
                </div>
              </div>
            )}

            {/* Portfolio nudge */}
            <div className="mt-16 pt-14 border-t border-[var(--sr-border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="max-w-[520px]">
                <p className="eyebrow">Follow Along</p>
                <p className="font-serif text-2xl text-[color:var(--sr-ivory)] mt-2 tracking-[-0.005em]">
                  Fresh looks and before/afters daily on Instagram.
                </p>
              </div>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-instagram-${service.id}`}
                className="btn-outline"
              >
                <Instagram size={14} strokeWidth={1.5} />
                @skinrefinery.417
              </a>
            </div>

            {/* Q&A */}
            <div className="mt-20 pt-14 border-t border-[var(--sr-border)]">
              <p className="eyebrow">Q&amp;A</p>
              <h3 className="font-serif text-3xl lg:text-[38px] text-[color:var(--sr-ivory)] mt-3 tracking-[-0.005em]">
                Frequently Asked Questions
              </h3>
              <div className="mt-10 divide-y divide-[var(--sr-border)]">
                {service.faqs.map((f, i) => (
                  <FaqItem key={i} q={f.q} a={f.a} testId={`${service.id}-faq-${i}`} />
                ))}
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-20 text-center">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid={`service-book-bottom-${service.id}`}
              >
                Book Your {service.title}
                <ArrowRight size={14} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ q, a, testId }) {
  return (
    <details className="group py-6" data-testid={testId}>
      <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
        <span className="font-serif text-lg lg:text-xl text-[color:var(--sr-ivory)] pr-4">
          {q}
        </span>
        <ChevronDown
          size={18}
          strokeWidth={1.5}
          className="mt-1 text-[color:var(--sr-gold)] transition-transform duration-500 group-open:rotate-180 shrink-0"
        />
      </summary>
      <p className="mt-4 text-[color:var(--sr-beige)] text-[14.5px] leading-[1.85] max-w-[820px]">
        {a}
      </p>
    </details>
  );
}
